import { useEffect, useState, type CSSProperties } from "react";

interface Props {
  active: string;
  intros: Record<string, { title: string; lines: string[] }>;
  /** Auto-dismiss after ms. Default 6000. 0 = stay forever. */
  autoCloseMs?: number;
}

const wrapStyle: CSSProperties = {
  position: "fixed",
  top: "12vh",
  left: "50%",
  transform: "translateX(-50%)",
  background: "rgba(15,18,24,0.85)",
  border: "1px solid rgba(168,85,247,0.5)",
  borderRadius: 8,
  padding: "10px 14px",
  color: "#e6e8ee",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 13,
  lineHeight: 1.45,
  textAlign: "center",
  pointerEvents: "auto",
  zIndex: 1100,
  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
  maxWidth: "min(620px, 90vw)",
};

/**
 * Auto-dismissing intro/legend overlay shown when the active demo changes.
 * Per-demo content is configured by the host. Click ✕ or wait `autoCloseMs`
 * to dismiss. Re-shows on demo change.
 */
export function SceneIntro({ active, intros, autoCloseMs = 6000 }: Props) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
    if (autoCloseMs > 0) {
      const t = setTimeout(() => setVisible(false), autoCloseMs);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active, autoCloseMs]);

  const intro = intros[active];
  if (!intro || !visible) return null;

  return (
    <div style={wrapStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <strong style={{ color: "#c4b5fd" }}>{intro.title}</strong>
        <button
          type="button"
          onClick={() => setVisible(false)}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#e6e8ee",
            borderRadius: 4,
            padding: "1px 7px",
            cursor: "pointer",
            fontSize: 11,
          }}
        >
          ✕
        </button>
      </div>
      <div style={{ marginTop: 6 }}>
        {intro.lines.map((l, i) => (
          <div key={i} style={{ opacity: i === 0 ? 1 : 0.85 }}>{l}</div>
        ))}
      </div>
    </div>
  );
}
